import { useEffect, useState } from 'react';
import { bookingService } from '../../services/booking';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/base/Button';
import { useNavigate } from 'react-router-dom';

import { generateInvoice } from '../../utils/pdfGenerator';

export default function MyBookings() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [bookings, setBookings] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) {
            navigate('/auth/login');
            return;
        }
        fetchMyBookings();
    }, [user, navigate]);

    const fetchMyBookings = async () => {
        try {
            if (!user) return;
            const data = await bookingService.getUserBookings(user.id);
            setBookings(data);
        } catch (error) {
            console.error('Error fetching bookings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelBooking = async (bookingId: string) => {
        if (window.confirm('Are you sure you want to cancel this booking?')) {
            try {
                await bookingService.cancelBooking(bookingId);
                fetchMyBookings(); // Refresh list
            } catch (error) {
                console.error('Error cancelling booking:', error);
                alert('Failed to cancel booking');
            }
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const formatCurrency = (amount: number, currency: string) => {
        if (currency === 'NGN') return `NGN ${amount.toLocaleString()}`;
        return `$${amount.toLocaleString()}`;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 pt-24 pb-12 px-4 flex justify-center">
                <div className="text-gray-600">Loading your bookings...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pt-24 pb-12 px-4">
            <div className="max-w-4xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">My Bookings</h1>
                    <Button onClick={() => navigate('/hajj-umrah')}>Book New Trip</Button>
                </div>

                {bookings.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-md p-12 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="ri-suitcase-line text-3xl text-gray-400"></i>
                        </div>
                        <h3 className="text-xl font-medium text-gray-900 mb-2">No bookings yet</h3>
                        <p className="text-gray-500 mb-6">You haven't made any bookings yet. Start your spiritual journey today.</p>
                        <Button onClick={() => navigate('/hajj-umrah')}>Browse Packages</Button>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {bookings.map((booking) => (
                            <div key={booking.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                                <div className="p-6 border-b border-gray-100 flex flex-wrap justify-between items-center gap-4">
                                    <div>
                                        <p className="text-sm text-gray-500">Booking ID: <span className="font-mono text-gray-700">{booking.id.slice(0, 8)}</span></p>
                                        <p className="text-sm text-gray-500">Date: {formatDate(booking.created_at)}</p>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                            booking.status === 'paid' ? 'bg-green-100 text-green-800' :
                                            booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                            booking.status === 'refund_pending' ? 'bg-orange-100 text-orange-800' :
                                            booking.status === 'refunded' ? 'bg-gray-100 text-gray-800' :
                                            'bg-red-100 text-red-800'
                                        }`}>
                                            {booking.status.toUpperCase().replace('_', ' ')}
                                        </span>

                                        {['pending', 'paid'].includes(booking.status) && (
                                            <button
                                                onClick={() => handleCancelBooking(booking.id)}
                                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                                            >
                                                Cancel
                                            </button>
                                        )}

                                        <button
                                            onClick={() => generateInvoice(booking)}
                                            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                                        >
                                            <i className="ri-download-line mr-1"></i>
                                            Invoice
                                        </button>
                                    </div>
                                </div>

                                <div className="p-6">
                                    <div className="flex justify-between items-start mb-6">
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-900 capitalize">{booking.package_type} Package</h3>
                                            <p className="text-gray-600">{booking.booking_travelers.length} Traveler(s)</p>
                                        </div>
                                        <p className="text-2xl font-bold text-blue-600">
                                            {formatCurrency(booking.total_amount, booking.currency)}
                                        </p>
                                    </div>

                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="font-medium text-gray-900 mb-3">Itinerary</h4>
                                        <div className="space-y-3">
                                            {booking.booking_items.map((item: any) => (
                                                <div key={item.id} className="flex items-start space-x-3 text-sm">
                                                    <i className={`mt-1 text-gray-400 ${
                                                        item.service_type === 'hotel' ? 'ri-hotel-line' :
                                                        item.service_type === 'flight' ? 'ri-plane-line' : 'ri-taxi-line'
                                                    }`}></i>
                                                    <div>
                                                        <p className="font-medium text-gray-900 capitalize">{item.service_type}</p>
                                                        <p className="text-gray-500">
                                                            {item.service_type === 'hotel' && `${item.details.destination} - ${item.details.rooms} Room(s)`}
                                                            {item.service_type === 'flight' && `${item.details.from} -> ${item.details.to}`}
                                                            {item.service_type === 'transport' && `${item.details.pickup} -> ${item.details.dropoff}`}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
